import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  LuCrosshair,
  LuGitMerge,
  LuPencil,
  LuRuler,
  LuScissors,
  LuMinusSquare,
  LuTag,
  LuTrash2,
} from 'react-icons/lu';
import type { IconType } from 'react-icons';

function ic(Icon: IconType, size = 14) {
  const C = Icon as React.ComponentType<{ size?: number }>;
  return <C size={size} />;
}

// ─────────────────────────────────────────────────────────────
// PaddockContextMenu
// Right-click context menu anchored at the cursor position.
// Only shown in view mode (enforced by the caller).
// ─────────────────────────────────────────────────────────────

interface Props {
  /** Cursor position relative to the map container (from e.point). */
  x: number;
  y: number;
  onClose: () => void;
  onCenter: () => void;
  onRename: () => void;
  onUpdateArea: () => void;
  onEdit: () => void;
  /** Disabled when the selected paddock has no adjacent neighbours. */
  mergeDisabled: boolean;
  onMerge: () => void;
  onSplit: () => void;
  onCutout: () => void;
  onDelete: () => void;
}

const PaddockContextMenu = ({
  x,
  y,
  onClose,
  onCenter,
  onRename,
  onUpdateArea,
  onEdit,
  mergeDisabled,
  onMerge,
  onSplit,
  onCutout,
  onDelete,
}: Props) => {
  const run = (fn: () => void) => () => {
    onClose();
    fn();
  };

  const items: MenuProps['items'] = [
    {
      key: 'center',
      icon: ic(LuCrosshair),
      label: 'Center Selection',
      onClick: run(onCenter),
    },
    { type: 'divider' },
    {
      key: 'rename',
      icon: ic(LuTag),
      label: 'Rename Paddock',
      onClick: run(onRename),
    },
    {
      key: 'updateArea',
      icon: ic(LuRuler),
      label: 'Update Area',
      onClick: run(onUpdateArea),
    },
    {
      key: 'edit',
      icon: ic(LuPencil),
      label: 'Edit Boundary',
      onClick: run(onEdit),
    },
    {
      key: 'merge',
      icon: ic(LuGitMerge),
      label: 'Merge Adjacent Boundaries',
      disabled: mergeDisabled,
      onClick: run(onMerge),
    },
    {
      key: 'split',
      icon: ic(LuScissors),
      label: 'Split Boundary',
      onClick: run(onSplit),
    },
    {
      key: 'cutout',
      icon: ic(LuMinusSquare),
      label: 'Add Internal Cutout',
      onClick: run(onCutout),
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: ic(LuTrash2),
      label: 'Delete Boundary',
      danger: true,
      onClick: run(onDelete),
    },
  ];

  return (
    <Dropdown
      open
      menu={{ items }}
      placement="bottomLeft"
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/*
       * Zero-sized anchor div at the cursor position.
       * Dropdown renders its overlay at the bottom-left of this anchor,
       * which puts the menu top-left corner exactly at the cursor.
       * Dynamic left/top values require inline style (overlay positioning).
       */}
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
      />
    </Dropdown>
  );
};

export default PaddockContextMenu;
